import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { Instant } from "@js-joda/core";
import React, { ReactElement } from "react";
import { VisitBroadcastId } from "../../messages/ObjectIds";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { CHEVRON_RIGHT_ICON } from "../../utils/svgs_new";
import { AppIcon } from "../Generic/AppIcon";
import { If } from "../Generic/If";
import { isDefined } from "../Generic/IsDefined";
import { VisitPendingConfirmationCountdown } from "./VisitPendingConfirmationCountdown";
import { default as WomanWithBaloonsVector } from "/public/assets/woman-with-baloons-and-phone.svg?react";

interface Props {
  visitId: VisitBroadcastId;
  pendingConfirmationUntil: Instant;
  pendingConfirmationConfirmedAt: Instant | null;
  onShowDetailsClick: (visitId: VisitBroadcastId, isReviewOnly: boolean) => void;
  onTimerEnd: () => void;
}

export function VisitPendingConfirmationBanner(props: Props): ReactElement {
  const isConfirmedVisit = isDefined(props.pendingConfirmationConfirmedAt);

  const handleConfirmClick = () => props.onShowDetailsClick(props.visitId, false);
  const handleShowDetailsClick = () => props.onShowDetailsClick(props.visitId, true);

  return (
    <BannerCard>
      <BannerContent>
        <If condition={!isConfirmedVisit}>
          <BannerText className="bold">
            {translate(STRINGS.YAY_YOU_WERE_ASSIGNED_TO_A_VISIT)}
          </BannerText>
          <BannerText>
            <VisitPendingConfirmationCountdown
              text={translate(STRINGS.YOU_HAVE_HH_MM_TO_CONFIRM_IT)}
              pendingConfirmationUntil={props.pendingConfirmationUntil}
              onTimerEnd={props.onTimerEnd}
            />
          </BannerText>
          <BannerButton onClick={handleConfirmClick}>
            <span>{translate(STRINGS.CONFIRM)}</span>
            <AppIcon icon={CHEVRON_RIGHT_ICON} fill="var(--primary-color)" size={12} />
          </BannerButton>
        </If>
        <If condition={isConfirmedVisit}>
          <BannerText className="bold">
            {translate(STRINGS.WE_WILL_SEND_YOU_THE_FINAL_CONFIRMATION_SOON)}
          </BannerText>
          <BannerText>
            <VisitPendingConfirmationCountdown
              text={translate(STRINGS.WE_GOT_YOUR_RESPONSE_WAIT_HH_MM)}
              pendingConfirmationUntil={props.pendingConfirmationUntil}
              onTimerEnd={props.onTimerEnd}
            />
          </BannerText>
          <BannerButton onClick={handleShowDetailsClick}>
            <span>{translate(STRINGS.SHOW_DETAILS)}</span>
            <AppIcon icon={CHEVRON_RIGHT_ICON} fill="var(--primary-color)" size={12} />
          </BannerButton>
        </If>
      </BannerContent>
      <BannerImageContainer>
        <WomanWithBaloonsVector height={114} />
      </BannerImageContainer>
    </BannerCard>
  );
}

const cardSlideIn = keyframes`
  from {
    transform: translateY(-50%);
    opacity: 0;
  }
`;

const BannerCard = styled.div`
  background-color: var(--primary-color);
  color: white;
  display: flex;
  justify-content: space-between;
  font-weight: 500;
  border-radius: 12px;
  box-shadow: 0px 5px 20px rgb(0 0 0 / 5%);
  transition: box-shadow 250ms ease;
  animation: ${cardSlideIn} 350ms cubic-bezier(0.18, 1.1, 0.64, 1.13) backwards;
`;

const BannerContent = styled.div`
  padding: 16px 0 16px 16px;
  flex-grow: 2;
`;

const BannerImageContainer = styled.div`
  margin-top: -8px;
  margin-left: -8px;
`;

const BannerText = styled.div`
  & + & {
    margin-top: 4px;
  }

  &.bold {
    font-weight: 800;
  }
`;

const BannerButton = styled.button`
  height: 32px;
  margin-top: 10px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  background-color: white;
  border: none;
  border-radius: 12px;
  box-sizing: border-box;
  font-weight: 800;
  font-size: 14px;
  line-height: 120%;
  color: var(--primary-color);

  .AppIcon {
    margin-left: 4px;
  }
`;
